import { type CSSProperties, type ReactElement, useEffect, useState } from 'react'
import { frameOf } from '@/features/shop/skins'
import type { BoardGeometry, PlacedToken, PlayerColor } from '@/shared/board/types'
import type { GamePlayer } from '@/shared/game/types'
import type { CameraFrame } from './usePhoneCamera'

/**
 * Обвязка партии на телефоне в портрете (usePhoneCamera): полоса игроков
 * сверху, мини-карта и статус снизу, меню в «⋯».
 *
 * Шапка сайта в партии на телефоне скрыта: логотип и значки занимали строку,
 * а кто с кем играет и чей ход, видно не было — тем более когда камера
 * подъехала к своему углу доски.
 */

const PLAYER: Record<PlayerColor, string> = {
  red: 'var(--red)',
  green: 'var(--green)',
  yellow: 'var(--yellow)',
  blue: 'var(--blue)',
}

/** Кольцо из пяти долей: сколько фишек уже дома. */
function homeRing(home: number, colour: string): ReactElement[] {
  const parts: ReactElement[] = []

  for (let at = 0; at < 5; at++) {
    const from = -Math.PI / 2 + (at * 2 * Math.PI) / 5 + 0.14
    const to = from + (2 * Math.PI) / 5 - 0.28
    const point = (angle: number) => `${20 + 18 * Math.cos(angle)} ${20 + 18 * Math.sin(angle)}`

    parts.push(
      <path
        key={`home-${at}`}
        d={`M ${point(from)} A 18 18 0 0 1 ${point(to)}`}
        fill="none"
        stroke={colour}
        strokeOpacity={at < home ? 1 : 0.22}
        strokeWidth={3.2}
        strokeLinecap="round"
      />,
    )
  }

  return parts
}

/**
 * Аватарка 26 пикселей: картинка, первая буква или значок компьютера.
 * Вплотную — рамка из магазина, снаружи — кольцо дома в цвете игрока: у рамки
 * цвет материала, у кольца цвет игрока, и спутать их нельзя.
 */
function Face({ player, home }: { player: GamePlayer; home: number }) {
  const colour = player.color === null ? 'var(--muted)' : PLAYER[player.color]
  const frame = frameOf(player.skins?.frame)
  const clip = `face-${player.seat}`

  return (
    <svg className="pl__face" viewBox="0 0 40 40" width="40" height="40" aria-hidden="true">
      {homeRing(home, colour)}
      <circle cx="20" cy="20" r="13" fill={colour} />
      {player.avatar !== null ? (
        <>
          <clipPath id={clip}>
            <circle cx="20" cy="20" r="13" />
          </clipPath>
          <image
            href={player.avatar}
            x="7"
            y="7"
            width="26"
            height="26"
            clipPath={`url(#${clip})`}
            preserveAspectRatio="xMidYMid slice"
          />
        </>
      ) : player.bot !== null ? (
        <>
          <rect
            x="13.5"
            y="14"
            width="13"
            height="9"
            rx="1.5"
            fill="none"
            stroke="#16181a"
            strokeWidth="1.8"
          />
          <path d="M17 26.5 H23" stroke="#16181a" strokeWidth="1.8" strokeLinecap="round" />
        </>
      ) : (
        <text x="20" y="24.6" fontSize="13" fontWeight="900" textAnchor="middle" fill="#16181a">
          {[...player.name][0]?.toUpperCase() ?? '?'}
        </text>
      )}
      {frame !== null && (
        <>
          <circle cx="20" cy="20" r="13.6" fill="none" stroke={frame.ring} strokeWidth="2.2" />
          <circle cx="20" cy="20" r="12.4" fill="none" stroke={frame.edge} strokeWidth="0.6" />
        </>
      )}
    </svg>
  )
}

/**
 * Полоса игроков: аватарка, кольцо дома, ник. Рамкой — тот, чей ход.
 *
 * Чип не шире своей доли: ник бывает до 32 символов у учётной записи и до
 * 40 у гостя, и он обрезается многоточием, а не раздвигает полосу.
 */
export function PlayersBar({
  players,
  homes,
  current,
  menu,
}: {
  players: GamePlayer[]
  homes: Partial<Record<PlayerColor, number>>
  current: PlayerColor | null
  menu: { label: string; run: () => void }[]
}) {
  const [open, setOpen] = useState(false)

  return (
    <div className="players">
      {players.map((player) => (
        <div
          key={player.seat}
          className={`pl${player.color !== null && player.color === current ? ' pl--turn' : ''}`}
          style={
            {
              '--c': player.color === null ? 'var(--muted)' : PLAYER[player.color],
            } as CSSProperties
          }
          title={player.name}
        >
          <Face player={player} home={player.color === null ? 0 : (homes[player.color] ?? 0)} />
          <b>{player.name}</b>
        </div>
      ))}
      <div className="players__more">
        <button
          className="ghost"
          type="button"
          aria-label="меню партии"
          aria-expanded={open}
          onClick={() => setOpen(!open)}
        >
          ⋯
        </button>
        {open && (
          <div className="players__menu" role="menu">
            {menu.map((item) => (
              <button
                key={item.label}
                className="ghost"
                type="button"
                role="menuitem"
                onClick={() => {
                  setOpen(false)
                  item.run()
                }}
              >
                {item.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * Мини-карта: силуэт кольца, точки фишек и рамка того, что видно в окне.
 * Без подписей — она про «где», а не про «кто».
 */
export function MiniMap({
  board,
  tokens,
  frame,
}: {
  board: BoardGeometry
  tokens: PlacedToken[]
  frame: CameraFrame
}) {
  const side = board.size + 1
  /*
   * Поле вокруг доски. Без него фишки в угловых клетках (в меандре там
   * старты) и рамка обзора у края выходили за картинку и срезались её
   * скруглёнными углами.
   */
  const margin = 1

  return (
    <svg
      className="minimap"
      viewBox={`${-0.5 - margin} ${-0.5 - margin} ${side + margin * 2} ${side + margin * 2}`}
      aria-hidden="true"
    >
      <rect
        x={-0.5 - margin}
        y={-0.5 - margin}
        width={side + margin * 2}
        height={side + margin * 2}
        rx="2"
        fill="var(--plate)"
      />
      {board.ring.map((cell) => (
        <rect
          key={`m-${cell.row}-${cell.col}`}
          x={cell.col + 0.15}
          y={cell.row + 0.15}
          width="0.7"
          height="0.7"
          fill="var(--road)"
        />
      ))}
      {tokens
        .filter((token) => !token.waiting)
        .map((token) => (
          <circle
            key={`m-${token.color}-${token.token}`}
            cx={token.cell.col + 0.5}
            cy={token.cell.row + 0.5}
            r="0.6"
            fill={PLAYER[token.color]}
          />
        ))}
      <rect
        x={frame.x * side - 0.5}
        y={frame.y * side - 0.5}
        width={frame.w * side}
        height={frame.h * side}
        rx="0.4"
        fill="none"
        stroke="var(--ink)"
        strokeOpacity="0.8"
        strokeWidth="0.35"
      />
    </svg>
  )
}

/**
 * Партия на телефоне идёт на весь экран: шапка сайта прячется (html.phone-play
 * в ui.css). Классом на корне, а не пропсом: шапка живёт в App, выше экрана
 * партии, и тянуть туда состояние партии ради одного класса незачем.
 */
export function PhonePlay(): null {
  useEffect(() => {
    document.documentElement.classList.add('phone-play')

    return () => document.documentElement.classList.remove('phone-play')
  }, [])

  return null
}
